import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { ConfilexMark, ConfilexWordmark } from "@/components/ConfilexLogo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const presetRole = params.get("role") === "expert" ? "expert" : "client";
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    role: presetRole, phone: "", city: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Si l'URL change après le montage (rare), on resynchronise le rôle.
  useEffect(() => {
    if (presetRole !== form.role) {
      setForm(f => ({ ...f, role: presetRole }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetRole]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) {
      toast.success("Compte créé");
      // Parcours spécialiste : on dirige vers /pricing pour choisir l'abonnement,
      // puis le spécialiste remplit son dossier (ExpertApply) et règle. La validation
      // CONFILEX s'opère ensuite côté admin (parcours pending → activation).
      nav(form.role === "expert" ? "/pricing?from=signup" : "/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B3A2A] text-white flex items-center justify-center p-6 grain relative overflow-hidden">
      <Seo title="Création de compte" description="Créez votre compte CONFILEX (client ou spécialiste) — accédez à l'orientation guidée et à l'Audit Documentaire Intelligent." canonical="/register" noindex />
      <div className="absolute inset-0 opacity-30">
        <img src="https://images.unsplash.com/photo-1590509038652-23272b923485?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3A2A]/60 to-[#0B3A2A]"></div>
      </div>
      <div className="relative w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <ConfilexMark size={40} theme="light" />
          <ConfilexWordmark size={22} theme="light" />
        </Link>
        <div className="glass-dark rounded-2xl p-8 fade-up">
          <h1 className="font-serif text-3xl mb-2">Créer un compte</h1>
          <p className="text-white/60 text-sm mb-8">Rejoignez la plateforme professionnelle CONFILEX.</p>
          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label className="text-white/80 mb-3 block">Je suis</Label>
              <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-2 gap-3">
                <label className={`rounded-xl border p-4 cursor-pointer transition-smooth ${form.role==="client" ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/10 bg-white/5"}`}>
                  <RadioGroupItem value="client" id="r-client" className="sr-only" />
                  <div className="text-sm font-medium">Demandeur</div>
                  <div className="text-xs text-white/60">Je sollicite un accompagnement</div>
                </label>
                <label className={`rounded-xl border p-4 cursor-pointer transition-smooth ${form.role==="expert" ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/10 bg-white/5"}`}>
                  <RadioGroupItem value="expert" id="r-expert" className="sr-only" />
                  <div className="text-sm font-medium">Spécialiste</div>
                  <div className="text-xs text-white/60">Je souhaite être référencé</div>
                </label>
              </RadioGroup>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 mb-2 block">Prénom</Label>
                <Input required value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="reg-first-name"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Nom</Label>
                <Input required value={form.last_name} onChange={e => set("last_name", e.target.value)} data-testid="reg-last-name"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
              </div>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Email</Label>
              <Input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="reg-email"
                className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Mot de passe</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={e => set("password", e.target.value)} data-testid="reg-password"
                className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 mb-2 block">Téléphone (optionnel)</Label>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="reg-phone"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Ville</Label>
                <Input value={form.city} onChange={e => set("city", e.target.value)} data-testid="reg-city"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full h-11 rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
          </form>
          <p className="text-sm text-white/60 mt-6 text-center">
            Déjà inscrit ? <Link to="/login" className="text-[#C9A961] hover:underline">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
